import useAdminAccess from "hooks/useAdminAccess";
import AdminPermission from "components/admin/AdminPermission";
import {
  httpGetUserVirtualCards,
  httpFreezeVirtualCard,
  httpDeleteVirtualCard,
} from "api/admin/virtual-card";
import AdminSidebar from "components/layout/AdminSidebar";
import Container from "components/layout/Container";
import Main from "components/layout/Main";
import { useState } from "react";
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { showDanger, showSuccess } from "utilities/misc";

interface VirtualCard {
  id: number;
  firstSix: string;
  lastFour: string;
  brand: string | null;
  currency: string;
  status: string;
  balance: number;
  isAdminFrozen: boolean;
}

const VirtualCards: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { can } = useAdminAccess();
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<number | null>(null);
  const [showFreezeModal, setShowFreezeModal] = useState<{
    cardId: number;
    isFrozen: boolean;
  } | null>(null);

  const { data: cardsData, isLoading, refetch } = useQuery(
    ["virtualCards", userId],
    () => httpGetUserVirtualCards(userId || ""),
    {
      enabled: !!userId,
    }
  );

  const cards: VirtualCard[] = cardsData?.cards || [];

  const handleCardClick = (cardId: number) => {
    navigate(`/virtual-cards/${userId}/card/${cardId}`);
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleFreezeCard = async () => {
    if (!showFreezeModal) return;

    try {
      setIsActionLoading(true);
      await httpFreezeVirtualCard(
        showFreezeModal.cardId.toString(),
        !showFreezeModal.isFrozen
      );
      showSuccess(
        `Card ${!showFreezeModal.isFrozen ? "frozen" : "unfrozen"} successfully`
      );
      setShowFreezeModal(null);
      refetch();
    } catch (error) {
      showDanger("Failed to update card freeze status");
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDeleteCard = async (cardId: number) => {
    try {
      setIsActionLoading(true);
      await httpDeleteVirtualCard(cardId.toString());
      showSuccess("Card deleted successfully");
      setShowDeleteModal(null);
      refetch();
    } catch (error) {
      showDanger("Failed to delete card");
    } finally {
      setIsActionLoading(false);
    }
  };

  return (
    <Container>
      <AdminSidebar />
      <Main className="mx-5 mt-5 text-left">
        <div className="flex items-center gap-4 mb-5">
          <button
            onClick={handleBackClick}
            className="px-4 py-2 bg-gray-200 text-black rounded hover:bg-gray-300"
          >
            ← Back
          </button>
          <h3 className="text-2xl">Virtual Cards</h3>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {isLoading ? (
            <p className="text-center py-10">Loading virtual cards...</p>
          ) : cards.length === 0 ? (
            <p className="text-center py-10 text-gray-500">
              No virtual cards found for this user.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-100 border-b">
                  <tr>
                    <th className="px-8 py-4 text-left text-base font-semibold text-gray-900">
                      Card Number
                    </th>
                    <th className="px-8 py-4 text-left text-base font-semibold text-gray-900">
                      Brand
                    </th>
                    <th className="px-8 py-4 text-left text-base font-semibold text-gray-900">
                      Currency
                    </th>
                    <th className="px-8 py-4 text-left text-base font-semibold text-gray-900">
                      Balance
                    </th>
                    <th className="px-8 py-4 text-left text-base font-semibold text-gray-900">
                      Status
                    </th>
                    <th className="px-8 py-4 text-left text-base font-semibold text-gray-900">
                      Frozen
                    </th>
                    <th className="px-8 py-4 text-left text-base font-semibold text-gray-900">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {cards.map((card) => (
                    <tr
                      key={card.id}
                      className="border-b hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-8 py-5 text-base text-gray-900 font-mono">
                        {card.firstSix} •••• •••• {card.lastFour}
                      </td>
                      <td className="px-8 py-5 text-base text-gray-900">
                        {card.brand || "—"}
                      </td>
                      <td className="px-8 py-5 text-base text-gray-900">
                        {card.currency}
                      </td>
                      <td className="px-8 py-5 text-base font-semibold text-gray-900">
                        {card.balance} {card.currency}
                      </td>
                      <td className="px-8 py-5 text-base">
                        <span
                          className={`px-4 py-2 rounded-full text-sm font-semibold ${
                            card.status === "Card - Active"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {card.status}
                        </span>
                      </td>
                      <td className="px-8 py-5 text-base">
                        {card.isAdminFrozen ? (
                          <span className="px-4 py-2 rounded-full text-sm font-semibold bg-red-100 text-red-800">
                            ⚠️ Frozen
                          </span>
                        ) : (
                          <span className="px-4 py-2 rounded-full text-sm font-semibold bg-gray-100 text-gray-800">
                            Active
                          </span>
                        )}
                      </td>
                      <td className="px-8 py-5 text-base space-x-2 flex">
                        <button
                          onClick={() => handleCardClick(card.id)}
                          className="px-4 py-2 bg-gray-300 text-gray-900 rounded hover:bg-gray-400 text-sm font-semibold"
                        >
                          Transactions
                        </button>
                        {can(card.isAdminFrozen ? "virtualCard.cards.unfreeze" : "virtualCard.cards.freeze") && (<button
                          onClick={() =>
                            setShowFreezeModal({
                              cardId: card.id,
                              isFrozen: card.isAdminFrozen,
                            })
                          }
                          disabled={isActionLoading}
                          className="px-4 py-2 bg-gray-300 text-gray-900 rounded hover:bg-gray-400 text-sm font-semibold disabled:opacity-50"
                        >
                          {card.isAdminFrozen ? "Unfreeze" : "Freeze"}
                        </button>)}
                        <AdminPermission permission={"virtualCard.cards.delete"}>
                          <button
                            onClick={() => setShowDeleteModal(card.id)}
                            disabled={isActionLoading}
                            className="px-4 py-2 bg-gray-300 text-gray-900 rounded hover:bg-gray-400 text-sm font-semibold disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </AdminPermission>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {showFreezeModal && can(showFreezeModal.isFrozen ? "virtualCard.cards.unfreeze" : "virtualCard.cards.freeze") && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                {showFreezeModal.isFrozen ? "Unfreeze Card" : "Freeze Card"}
              </h2>
              <p className="text-gray-600 mb-6">
                Are you sure you want to{" "}
                <span className="font-bold">
                  {showFreezeModal.isFrozen ? "unfreeze" : "freeze"}
                </span>{" "}
                this card?
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => setShowFreezeModal(null)}
                  disabled={isActionLoading}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-900 rounded hover:bg-gray-400 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleFreezeCard}
                  disabled={isActionLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {isActionLoading
                    ? "Processing..."
                    : showFreezeModal.isFrozen
                    ? "Unfreeze"
                    : "Freeze"}
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeleteModal && can("virtualCard.cards.delete") && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                Terminate Card
              </h2>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this card? This action is{" "}
                <span className="font-bold text-red-600">irreversible</span> and
                cannot be undone.
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => setShowDeleteModal(null)}
                  disabled={isActionLoading}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-900 rounded hover:bg-gray-400 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteCard(showDeleteModal)}
                  disabled={isActionLoading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                >
                  {isActionLoading ? "Deleting..." : "Delete Card"}
                </button>
              </div>
            </div>
          </div>
        )}
      </Main>
    </Container>
  );
};

export default VirtualCards;
